import type { OfferId } from "@pax/api-contract/payments/v1alpha1";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  focusHaloFlush,
  HapticSurface,
  panelCard,
  panelMotion,
  press,
  Radio,
  RadioGroup,
  Skeleton,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
} from "@pax/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Info, Lock } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSession } from "@/features/auth/queries";
import { api, reachable, requestErrorMessage } from "@/lib/api";
import { useBillingPortal } from "../billing-portal";
import type { AutoTopUpDraft } from "../format";
import {
  type CreditOffer,
  creditsOf,
  formatMoney as money,
  type PackOffer,
  paymentMethodLabel,
} from "../format";
import {
  cachedCredits,
  creditBalanceQuery,
  mySubscriptionQuery,
  offersQuery,
  refreshTopUp,
  type ShownSubscription,
  topUpSettingsQuery,
} from "../queries";
import {
  CHECKOUT_CLOSE_PATH,
  CHECKOUT_RETURNED,
  markCheckoutStarted,
  openStripeTab,
  sendToStripe,
  signalAutoTopUpSuccess,
} from "../resume-after-checkout";
import { useStartCheckout } from "../use-start-checkout";
import { type AutoTopUpFooterMeta, AutoTopUpSection, autoTopUpPaused } from "./AutoTopUpSection";
import type { CheckoutReturnNoticeKind } from "./CheckoutReturnNotice";
import { Coin } from "./Coin";
import { ConfirmAutoTopUpDialog } from "./ConfirmAutoTopUpDialog";
import { ConfirmPurchaseDialog } from "./ConfirmPurchaseDialog";
import { PaymentsClosedNote } from "./PaymentsClosedNote";
import { SavedPaymentMethodSection } from "./SavedPaymentMethodSection";

/** The dialog's two tabs: Monthly and One-time. */
export type TopUpTab = "token_subscription" | "pack";

const POPULAR_OFFERS: ReadonlySet<OfferId> = new Set(["token_regular"]);

const creditCount = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });

/** How often the dialog looks again while an automatic charge is in flight. */
const CHARGING_POLL_MS = 2_000;

/** How often the footer checks whether the player closed the setup tab without finishing. */
const SETUP_TAB_CHECK_MS = 1_000;
/** How long a closed setup tab's report has to arrive before the footer stops waiting. */
const SETUP_REPORT_GRACE_MS = 5_000;

function centsPerCredit(offer: CreditOffer): number {
  return offer.priceCents / creditsOf(offer);
}

/** What each offer earns over the plainest way to buy credits, worked out from the catalog. */
function offerPerks(offer: CreditOffer, packs: CreditOffer[]): string[] {
  const perCredit = `${money(centsPerCredit(offer))} per credit`;
  if (packs.length === 0) return [perCredit];
  if (offer.kind === "token_subscription") {
    const cheapestPack = Math.min(...packs.map(centsPerCredit));
    const saving = Math.round((1 - centsPerCredit(offer) / cheapestPack) * 100);
    return saving > 0 ? [perCredit, `${saving}% less than any pack`] : [perCredit];
  }
  const basePack = packs.reduce((smallest, pack) =>
    pack.priceCents < smallest.priceCents ? pack : smallest,
  );
  const bonus = Math.round((centsPerCredit(basePack) / centsPerCredit(offer) - 1) * 100);
  return bonus > 0 ? [perCredit, `${bonus}% bonus credits`] : [perCredit];
}

/**
 * Where a player buys credits: a monthly plan or a one-off pack, paid with their saved payment method or on
 * Stripe Checkout, and where they set up automatic top-up. `title` and `description` let the
 * out-of-credits prompt say why it opened.
 */
export function TopUpDialog({
  open,
  onOpenChange,
  title,
  description = "Pick a monthly plan or a one-time pack.",
  initialTab,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  /** The tab it opens on. Without one, it picks from the player's plan and automatic top-up. */
  initialTab?: TopUpTab;
}) {
  const { data: session } = useSession();
  const userId = session?.user.id;
  const { data: credits } = useQuery({
    ...creditBalanceQuery(userId ?? ""),
    enabled: open && userId !== undefined,
  });
  const heading =
    title ??
    (credits !== undefined && credits <= 0 ? "You're out of credits!" : "Get more credits");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-2xl gap-5 overflow-y-auto">
        <DialogHeader className="flex-row items-center gap-3 pr-8">
          <Coin className="size-9" />
          <div className="flex min-w-0 flex-col gap-1.5">
            <DialogTitle className="text-lg">{heading}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </div>
        </DialogHeader>
        <TopUpBody active={open} initialTab={initialTab} onLeave={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

function TopUpBody({
  active,
  initialTab,
  onLeave,
}: {
  active: boolean;
  initialTab?: TopUpTab;
  onLeave: () => void;
}) {
  const queryClient = useQueryClient();
  const [pickedTab, setTab] = useState<TopUpTab | null>(initialTab ?? null);
  const [selected, setSelected] = useState<OfferId | null>(null);
  const [confirming, setConfirming] = useState<CreditOffer | null>(null);
  const [autoFooterMeta, setAutoFooterMeta] = useState<AutoTopUpFooterMeta | null>(null);
  const [confirmingAuto, setConfirmingAuto] = useState<AutoTopUpDraft | null>(null);
  const [autoSetupWaiting, setAutoSetupWaiting] = useState(false);
  const [setupMissed, setSetupMissed] = useState(false);
  const setupTab = useRef<Window | null>(null);
  const paid = useRef(false);
  const offers = useQuery({ ...offersQuery, enabled: active });
  const subscriptions = useQuery({ ...mySubscriptionQuery, enabled: active });
  const topUp = useQuery({
    ...topUpSettingsQuery,
    enabled: active,
    refetchInterval: query =>
      active && query.state.data?.autoState === "charging" ? CHARGING_POLL_MS : false,
  });
  const settings = topUp.data;
  const paymentsOpen = settings?.paymentsClosedReason === null;
  const savedMethodLabel = settings?.paymentMethod
    ? paymentMethodLabel(settings.paymentMethod)
    : null;
  const currentPlan = subscriptions.data?.plan.current ?? null;
  const planAlsoInAppStore = subscriptions.data?.plan.alsoInAppStore ?? null;
  // A subscriber already has a plan, so what they can still buy is a pack. A paused automatic
  // top-up lives with the packs and needs the player.
  const tab =
    pickedTab ?? (currentPlan || autoTopUpPaused(settings) ? "pack" : "token_subscription");
  const loading = offers.isPending || subscriptions.isPending;

  const { startCheckout, checkout } = useStartCheckout({ onStarted: onLeave });
  const { portal, openBilling } = useBillingPortal(onLeave);
  const autoSetup = useMutation({
    mutationFn: async ({
      draft,
      returnPath,
      tab,
    }: {
      draft: AutoTopUpDraft;
      returnPath: string;
      tab: Window | null;
    }) => {
      const { url } = await reachable(
        api.payments.autoTopUpSetups.create({
          body: { ...draft, returnPath },
        }),
      );
      return { url, tab };
    },
    onSuccess: ({ url, tab }) => {
      sendToStripe(tab, url);
      setSetupMissed(false);
      setupTab.current = tab;
      setAutoSetupWaiting(true);
    },
    onError: (_error, { tab }) => tab?.close(),
  });

  // The setup tab reports how it ended, after this page has read the settings again. A rule that
  // turned on closes the dialog; one whose first charge failed stays, showing why.
  useEffect(() => {
    if (!autoSetupWaiting) return;
    const returned = (event: Event) => {
      const kind = (event as CustomEvent<CheckoutReturnNoticeKind>).detail;
      if (kind !== "cancelled" && !kind.startsWith("auto_top_up")) return;
      setAutoSetupWaiting(false);
      setSetupMissed(kind === "auto_top_up_refused");
      const state = queryClient.getQueryData(topUpSettingsQuery.queryKey)?.autoState;
      if (state === "on" || state === "charging") {
        signalAutoTopUpSuccess();
        onLeave();
      }
    };
    window.addEventListener(CHECKOUT_RETURNED, returned);
    return () => window.removeEventListener(CHECKOUT_RETURNED, returned);
  }, [autoSetupWaiting, queryClient, onLeave]);

  // A setup tab closed without finishing sends no report, so the footer stops waiting on its own.
  useEffect(() => {
    if (!autoSetupWaiting) return;
    let grace: ReturnType<typeof setTimeout> | undefined;
    const check = setInterval(() => {
      if (grace || !setupTab.current?.closed) return;
      grace = setTimeout(() => setAutoSetupWaiting(false), SETUP_REPORT_GRACE_MS);
    }, SETUP_TAB_CHECK_MS);
    return () => {
      clearInterval(check);
      clearTimeout(grace);
    };
  }, [autoSetupWaiting]);

  const leaving =
    checkout.isPending ||
    checkout.isSuccess ||
    portal.isPending ||
    portal.isSuccess ||
    autoSetup.isPending;
  const purchaseControlsDisabled = leaving || !paymentsOpen;
  const failure = checkout.error ?? portal.error ?? autoSetup.error;

  const creditOffers = (offers.data ?? []).filter(
    (offer): offer is CreditOffer => offer.kind !== "patron",
  );
  const packs = creditOffers.filter((offer): offer is PackOffer => offer.kind === "pack");
  const plans = creditOffers.filter(offer => offer.kind === "token_subscription");
  const chosen = creditOffers.find(offer => offer.offerId === selected);
  const planLocked = tab === "token_subscription" && currentPlan !== null;
  const planName = (offerId: string) =>
    plans.find(plan => plan.offerId === offerId)?.name ?? "a plan";

  function changeTab(next: TopUpTab) {
    setTab(next);
    setSelected(null);
    checkout.reset();
  }

  const autoFooterMode = tab === "pack" && autoFooterMeta?.active === true;

  function startAutoSetup(draft: AutoTopUpDraft) {
    const stripeTab = openStripeTab();
    autoSetup.mutate({
      draft,
      returnPath: stripeTab
        ? CHECKOUT_CLOSE_PATH
        : window.location.pathname + window.location.search,
      tab: stripeTab,
    });
  }

  function primaryAction() {
    if (autoFooterMode && autoFooterMeta?.draft) {
      const draft = autoFooterMeta.draft;
      if (settings?.paymentMethod?.allowsAutomatic) {
        setConfirmingAuto(draft);
      } else {
        startAutoSetup(draft);
      }
      return;
    }
    if (!chosen) return;
    if (savedMethodLabel) {
      setConfirming(chosen);
      return;
    }
    startCheckout(chosen.offerId);
  }

  const primaryDisabled = autoFooterMode
    ? purchaseControlsDisabled || !autoFooterMeta || autoFooterMeta.disabled
    : !chosen || purchaseControlsDisabled;

  const footerSummary = (() => {
    if (chosen) {
      return (
        <>
          <span className="font-medium">{chosen.name}</span>
          <span className="text-muted-foreground">
            {" · "}
            {money(chosen.priceCents)}
            {chosen.kind === "token_subscription" ? " a month" : ""}
          </span>
        </>
      );
    }
    if (autoFooterMode && autoFooterMeta?.draft) {
      const pack = packs.find(p => p.offerId === autoFooterMeta.draft?.autoOfferId);
      if (pack) {
        return (
          <>
            <span className="font-medium">{pack.name}</span>
            <span className="text-muted-foreground">
              {" · "}
              {money(pack.priceCents)}
            </span>
          </>
        );
      }
    }
    return (
      <span className="text-muted-foreground">
        {tab === "token_subscription" ? "Choose a plan" : "Choose a pack"}
      </span>
    );
  })();

  const confirmingAutoPack =
    confirmingAuto && packs.find(pack => pack.offerId === confirmingAuto.autoOfferId);

  function primaryLabel(checkoutLabel: string, payLabel: string): string {
    if (autoFooterMode && autoFooterMeta) {
      if (autoFooterMeta.waitingForStripe) return "Waiting for Stripe…";
      return autoFooterMeta.savingEdits ? "Save auto top-up" : "Turn on auto top-up";
    }
    return checkout.isPending || checkout.isSuccess ? checkoutLabel : payLabel;
  }

  return (
    <>
      {settings?.paymentsClosedReason && (
        <PaymentsClosedNote reason={settings.paymentsClosedReason} />
      )}
      <Tabs value={tab} onValueChange={value => changeTab(value as TopUpTab)} className="gap-4">
        <TabsList aria-label="How to pay">
          <TabsTab value="token_subscription">Monthly</TabsTab>
          <TabsTab value="pack">One-time</TabsTab>
        </TabsList>

        <TabsPanel value="token_subscription" className="flex flex-col gap-3">
          {currentPlan && (
            <div className={cn(panelCard, "flex flex-wrap items-center gap-3 p-3 text-sm")}>
              <p className="min-w-0 flex-1 text-pretty">
                You're on <span className="font-medium">{planName(currentPlan.offerId)}</span>.{" "}
                {currentPlan.provider === "app_store"
                  ? "It's billed through the App Store, so switch tier or cancel in your iPhone's subscription settings."
                  : "Switch tier or cancel from billing."}
                {planAlsoInAppStore &&
                  ` You also pay for ${planName(planAlsoInAppStore.offerId)} through the App Store, so you're billed twice. Cancel the one you don't need here or in your iPhone's subscription settings.`}
              </p>
              {currentPlan.provider === "stripe" && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={purchaseControlsDisabled}
                  onClick={openBilling}
                >
                  Manage plan
                </Button>
              )}
            </div>
          )}
          <OfferChoices
            label="Monthly plans"
            offers={plans}
            packs={packs}
            loading={loading}
            failed={offers.isError}
            onRetry={() => void offers.refetch()}
            selected={selected}
            onSelect={setSelected}
            disabled={purchaseControlsDisabled || planLocked}
            currentOfferId={currentPlan?.offerId}
          />
          <p className="text-xs text-muted-foreground">
            Credits arrive each month. Cancel anytime.
          </p>
        </TabsPanel>

        <TabsPanel value="pack" className="flex flex-col gap-3">
          <OfferChoices
            label="Credit packs"
            offers={packs}
            packs={packs}
            loading={loading}
            failed={offers.isError}
            onRetry={() => void offers.refetch()}
            selected={selected}
            onSelect={setSelected}
            disabled={purchaseControlsDisabled}
          />
          <p className="text-xs text-muted-foreground">Pay once. No subscription.</p>
          {settings && (
            <AutoTopUpSection
              settings={settings}
              packs={packs}
              pickedPack={chosen?.kind === "pack" ? chosen : null}
              disabled={purchaseControlsDisabled}
              onConfirmWithBank={offerId => startCheckout(offerId)}
              onStartSetup={startAutoSetup}
              onFooterMetaChange={setAutoFooterMeta}
              setupWaiting={autoSetupWaiting}
              setupMissed={setupMissed}
            />
          )}
        </TabsPanel>
      </Tabs>

      <div className={cn(panelCard, "flex gap-2.5 p-3 text-xs text-muted-foreground")}>
        <Info aria-hidden className="mt-px size-4 shrink-0" />
        <p className="text-pretty">
          <span className="font-medium text-foreground">Why credits?</span> Every turn runs on AI
          models that are expensive to run. Credits cover that cost and keep Pax free to try.
        </p>
      </div>

      {subscriptions.data && (
        <PatronSection
          patron={subscriptions.data.patron}
          disabled={purchaseControlsDisabled}
          onManage={openBilling}
        />
      )}

      {settings && <SavedPaymentMethodSection settings={settings} disabled={leaving} />}

      {failure && (
        <p role="alert" className="text-sm text-destructive">
          {requestErrorMessage(failure)}
        </p>
      )}

      <DialogFooter className="flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <p aria-live="polite" className="w-full text-sm sm:mr-auto sm:w-auto">
          {footerSummary}
        </p>
        <div className="flex w-full flex-row flex-wrap gap-2 sm:ml-auto sm:w-auto sm:justify-end">
          {autoFooterMode ? (
            <Button
              type="button"
              size="lg"
              className="min-w-0 flex-1 px-4 sm:flex-none"
              disabled={primaryDisabled}
              onClick={primaryAction}
            >
              {primaryLabel("Opening checkout…", "Continue")}
            </Button>
          ) : savedMethodLabel ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="min-w-0 flex-1 px-3 sm:flex-none sm:px-4"
                disabled={!chosen || purchaseControlsDisabled}
                onClick={() => chosen && startCheckout(chosen.offerId)}
              >
                {checkout.isPending || checkout.isSuccess ? (
                  "Opening checkout…"
                ) : (
                  <>
                    <span className="sm:hidden">Other method</span>
                    <span className="hidden sm:inline">Use a different payment method</span>
                  </>
                )}
              </Button>
              <Button
                type="button"
                size="lg"
                className="min-w-0 flex-1 px-3 sm:flex-none sm:px-4"
                disabled={primaryDisabled}
                onClick={primaryAction}
              >
                {primaryLabel("Opening checkout…", `Pay with ${savedMethodLabel}`)}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              size="lg"
              className="min-w-0 flex-1 px-4 sm:flex-none"
              disabled={primaryDisabled}
              onClick={primaryAction}
            >
              {primaryLabel("Opening checkout…", "Continue")}
            </Button>
          )}
        </div>
        <span className="flex w-full items-center justify-center gap-1.5 text-xs text-muted-foreground sm:w-auto sm:justify-start">
          <Lock aria-hidden className="size-3.5 shrink-0" />
          Secured by Stripe
        </span>
      </DialogFooter>

      <ConfirmAutoTopUpDialog
        draft={confirmingAuto}
        pack={confirmingAutoPack ?? null}
        paymentMethodLabel={savedMethodLabel ?? ""}
        savingEdits={autoFooterMeta?.savingEdits === true}
        onClose={() => setConfirmingAuto(null)}
        onUseDifferentPaymentMethod={() => {
          const draft = confirmingAuto;
          setConfirmingAuto(null);
          if (draft) startAutoSetup(draft);
        }}
      />

      <ConfirmPurchaseDialog
        offer={confirming}
        paymentMethodLabel={savedMethodLabel ?? ""}
        cancellationWaiverRequired={settings?.cancellationWaiverRequired ?? true}
        onPaid={() => {
          paid.current = true;
          markCheckoutStarted(cachedCredits(queryClient));
          void refreshTopUp(queryClient);
        }}
        onClose={() => {
          setConfirming(null);
          if (paid.current) onLeave();
        }}
        onUseDifferentPaymentMethod={() => {
          const offer = confirming;
          setConfirming(null);
          if (offer) startCheckout(offer.offerId);
        }}
      />
    </>
  );
}

/** The player's Patron subscription, and where to change or cancel it. */
const PatronSection = ({
  patron: { current, alsoInAppStore },
  disabled,
  onManage,
}: {
  patron: ShownSubscription;
  disabled: boolean;
  onManage: () => void;
}) => {
  if (!current) return null;
  return (
    <div className={cn(panelCard, "flex flex-wrap items-center gap-3 p-3 text-sm")}>
      <p className="min-w-0 flex-1 text-pretty">
        You're a <span className="font-medium">Patron</span>.{" "}
        {current.provider === "app_store"
          ? "It's billed through the App Store, so cancel it in your iPhone's subscription settings."
          : "Thank you for backing Pax. Cancel anytime from billing."}
        {alsoInAppStore &&
          " You're also a Patron through the App Store, so you're billed twice. Cancel the one you don't need here or in your iPhone's subscription settings."}
      </p>
      {current.provider === "stripe" && (
        <Button variant="outline" size="sm" disabled={disabled} onClick={onManage}>
          Manage Patron
        </Button>
      )}
    </div>
  );
};

function OfferChoices({
  label,
  offers,
  packs,
  loading,
  failed,
  onRetry,
  selected,
  onSelect,
  disabled,
  currentOfferId,
}: {
  label: string;
  offers: CreditOffer[];
  packs: CreditOffer[];
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  selected: OfferId | null;
  onSelect: (offerId: OfferId) => void;
  disabled: boolean;
  currentOfferId?: string;
}) {
  if (loading) {
    return (
      <div className="grid gap-3 pt-2.5 sm:grid-cols-3">
        {[0, 1, 2].map(key => (
          <Skeleton key={key} className="h-36 rounded-xl" />
        ))}
      </div>
    );
  }
  if (failed) {
    return (
      <p className={cn(panelCard, "p-3 text-sm text-muted-foreground")}>
        Prices failed to load.{" "}
        <button
          type="button"
          onClick={onRetry}
          className="underline underline-offset-2 hover:text-foreground"
        >
          Retry
        </button>
      </p>
    );
  }

  return (
    <RadioGroup
      aria-label={label}
      value={selected}
      onValueChange={value => onSelect(value as OfferId)}
      disabled={disabled}
      className="grid gap-3 pt-2.5 sm:grid-cols-3"
    >
      {offers.map(offer => (
        <OfferCard
          key={offer.offerId}
          offer={offer}
          perks={offerPerks(offer, packs)}
          badge={
            offer.offerId === currentOfferId
              ? "Current plan"
              : POPULAR_OFFERS.has(offer.offerId)
                ? "Most popular"
                : undefined
          }
        />
      ))}
    </RadioGroup>
  );
}

function OfferCard({
  offer,
  perks,
  badge,
}: {
  offer: CreditOffer;
  perks: string[];
  badge?: string;
}) {
  const monthly = offer.kind === "token_subscription";
  return (
    <Radio
      value={offer.offerId}
      className={cn(
        panelCard,
        "relative flex flex-col items-start gap-3 p-4 text-left ring-1 ring-transparent hover:bg-foreground/10 data-checked:bg-foreground/10 data-checked:ring-foreground data-disabled:pointer-events-none data-disabled:opacity-60",
        panelMotion,
        press,
        focusHaloFlush,
      )}
    >
      {badge && (
        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 rounded-full bg-foreground px-2 py-0.5 text-[0.7rem] font-medium whitespace-nowrap text-background">
          {badge}
        </span>
      )}
      <span className="flex items-center gap-2 font-medium">
        <Coin className="size-4" />
        {offer.name}
      </span>
      <span className="flex items-baseline gap-1">
        <span className="text-xl font-semibold tabular-nums">{money(offer.priceCents)}</span>
        {monthly && <span className="text-xs text-muted-foreground">/ month</span>}
      </span>
      <span className="flex flex-col gap-1 text-xs text-muted-foreground">
        {/* A pack is named for what it holds; a plan needs saying. */}
        {monthly && (
          <span className="text-sm text-foreground tabular-nums">
            {creditCount.format(creditsOf(offer))} credits a month
          </span>
        )}
        {perks.map(perk => (
          <span key={perk}>{perk}</span>
        ))}
      </span>
      <HapticSurface />
    </Radio>
  );
}
